import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Trophy, 
  Calendar, 
  Users, 
  ArrowRight, 
  Filter,
  Search,
  ChevronRight,
  Sparkles,
  Award,
  Target
} from 'lucide-react';
import { useChampionships } from '../../hooks/useChampionships';
import { useSeason } from '../../contexts/SeasonContext';
import SeasonSelector from '../Common/SeasonSelector';

const ModernChampionshipsList: React.FC = () => {
  const { currentSeason } = useSeason();
  const { data: championships, isLoading, error } = useChampionships();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSport, setSelectedSport] = useState('all');
  const [filterBySeason, setFilterBySeason] = useState(true);

  const filteredChampionships = championships?.filter(champ => {
    const matchesSearch = champ.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         champ.sport.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSport = selectedSport === 'all' || champ.sport.slug === selectedSport;
    
    // Filter by season if enabled (championships that are active during the selected season)
    const matchesSeason = !filterBySeason || 
                         (champ.first_year <= currentSeason && 
                          (!champ.last_year || champ.last_year >= currentSeason));
    
    return matchesSearch && matchesSport && matchesSeason;
  }) || [];

  const sports = Array.from(new Set(championships?.map(c => c.sport.name) || []));

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen py-12">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full border border-primary/30 mb-6"
            >
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-primary text-xs font-semibold uppercase tracking-wider">
                Championship Database
              </span>
            </motion.div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="gradient-text">CHAMPIONSHIPS</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Explore the complete history of championships across all major sports leagues
            </p>
          </div>

          {/* Season Selector */}
          <div className="flex justify-center mb-6">
            <SeasonSelector />
          </div>

          {/* Filters */}
          <div className="glass-card p-6 mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search championships..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-surface/50 border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary transition-colors"
                />
              </div>
              
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  value={selectedSport}
                  onChange={(e) => setSelectedSport(e.target.value)}
                  className="pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                >
                  <option value="all">All Sports</option>
                  {Array.from(new Set(championships?.map(c => c.sport) || [])).map(sport => (
                    <option key={sport.slug} value={sport.slug}>
                      {sport.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6 text-center"
            >
              <Trophy className="w-8 h-8 text-primary mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {championships?.length || 0}
              </div>
              <div className="text-sm text-gray-400">Total Championships</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-card p-6 text-center"
            >
              <Users className="w-8 h-8 text-secondary mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {sports.length}
              </div>
              <div className="text-sm text-gray-400">Sports Covered</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass-card p-6 text-center"
            >
              <Calendar className="w-8 h-8 text-accent mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {championships && championships.length > 0
                  ? new Date().getFullYear() - Math.min(...championships.map(c => c.first_year))
                  : 0}+
              </div>
              <div className="text-sm text-gray-400">Years of History</div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Championships Grid */}
      <div className="max-w-7xl mx-auto">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <div className="spinner" />
          </div>
        ) : error ? (
          <div className="glass-card p-12 text-center">
            <Trophy className="w-16 h-16 text-error mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-bold text-white mb-2">Error Loading Championships</h3>
            <p className="text-gray-400">{error.message}</p>
          </div>
        ) : filteredChampionships.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No Championships Found</h3>
            <p className="text-gray-400">Try adjusting your search or filters</p>
          </div>
        ) : (
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredChampionships.map((championship) => (
              <motion.div
                key={championship.id}
                variants={item}
                whileHover={{ scale: 1.02 }}
                className="group"
              >
                <Link to={`/championships/${championship.slug}`}>
                  <div className="glass-card h-full p-6 border border-border hover:border-primary/50 transition-all duration-300 group-hover:shadow-[0_0_30px_rgba(0,255,136,0.15)]">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-3 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                        <Trophy className="w-6 h-6 text-primary" />
                      </div>
                      <span className="badge badge-primary text-xs">
                        {championship.sport.abbreviation}
                      </span>
                    </div>

                    {/* Content */}
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-primary transition-colors">
                      {championship.name}
                    </h3>
                    
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                      {championship.description || championship.official_name || 
                       `The premier ${championship.sport.name} championship`}
                    </p>

                    {/* Meta Info */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <Award className="w-4 h-4 mr-2" />
                        <span>{championship.trophy_name || 'Championship Trophy'}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-2" />
                        <span>Since {championship.first_year}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <Target className="w-4 h-4 mr-2" />
                        <span>{championship.level.name}</span>
                      </div>
                    </div>

                    {/* Action */}
                    <div className="flex items-center justify-between pt-4 border-t border-border/50">
                      <span className="text-sm font-semibold text-primary">
                        View Details
                      </span>
                      <ChevronRight className="w-4 h-4 text-primary group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ModernChampionshipsList;